/**
 * The shift opened on this device, remembered for the sale point handover guard
 * (SEL-30). A shift belongs to its seller, but its cash sits in this device's
 * drawer: when a different seller signs in here while that shift is still open,
 * they must not open a second shift on top of it. Stored per device, apart from
 * the seller session, because the memory has to outlive a sign-out.
 */
export type SalePointShift = {
    shiftId: string;
    sellerId: string;
};

const SALE_POINT_SHIFT_STORAGE_KEY = 'bos.pos.salePointShift';

const hasWindowStorage = () =>
    typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';

/** The remembered shift, or `null` when none is (or what is stored is unreadable). */
export const getSalePointShift = (): SalePointShift | null => {
    if (!hasWindowStorage()) {
        return null;
    }
    const rawValue = window.localStorage.getItem(SALE_POINT_SHIFT_STORAGE_KEY);
    if (!rawValue) {
        return null;
    }
    try {
        const parsed = JSON.parse(rawValue) as Partial<SalePointShift> | null;
        if (
            parsed &&
            typeof parsed.shiftId === 'string' &&
            parsed.shiftId.length > 0 &&
            typeof parsed.sellerId === 'string' &&
            parsed.sellerId.length > 0
        ) {
            return { shiftId: parsed.shiftId, sellerId: parsed.sellerId };
        }
    } catch {
        // Fall through to the cleanup below.
    }
    window.localStorage.removeItem(SALE_POINT_SHIFT_STORAGE_KEY);
    return null;
};

/** Remembers the shift just opened on this device, replacing any earlier one. */
export const rememberSalePointShift = (shift: SalePointShift): void => {
    if (!hasWindowStorage()) {
        return;
    }
    window.localStorage.setItem(
        SALE_POINT_SHIFT_STORAGE_KEY,
        JSON.stringify(shift),
    );
};

/** Forgets the remembered shift: it closed, or the server no longer shows it. */
export const forgetSalePointShift = (): void => {
    if (!hasWindowStorage()) {
        return;
    }
    window.localStorage.removeItem(SALE_POINT_SHIFT_STORAGE_KEY);
};
